import * as yup from 'yup';
import i18next from '../../../i18n';
import { MANCHESTER_BRANCH_ID, maxPasswordLength, minPasswordLength } from '../../constants';
import { CBCT, IMAGING2D, INTRAORAL } from '../options/appointmentForm.options';
import { nameValidator } from './fieldValidators.yup';
/**
 * yup utils - schema validation for forms using yup
 */

const emailRegExp =
	/^([0-9a-zA-Z]([-.\w]*[0-9a-zA-Z])*@([0-9a-zA-Z][-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$/;

const passwordRegExp = /^[^\*]+$/;

export const REQUIRED = i18next.t('forms.validators.required');
export const REQUIRED_STATIC = 'Required';
export const PASSWORD_MESSAGE =
	"Don't use * in password, You can use other special characters.";

export const OrderReportValidationSchema = yup.object().shape({
	patient: yup.string().required(`Patient type can't be blank.`),
	area: yup.string().required(`Reported areas can't be blank.`),
	type_report: yup.string().required(`Report type can't be blank.`),
	payment: yup.string().required(`Payment type can't be blank.`),
	comments_and_requests: yup.string().required(`Comment can't be blank.`),
	patient_title: yup.string().when('patient', {
		is: 'new_patient',
		then: yup.string().required('Required'),
	}),
	patient_name: yup.string().when('patient', {
		is: 'new_patient',
		then: yup.string().required('Required'),
	}),
	patient_surname: yup.string().when('patient', {
		is: 'new_patient',
		then: yup.string().required('Required'),
	}),
	patient_dob: yup
		.date()
		.nullable()
		.when('patient', {
			is: 'new_patient',
			then: yup.date().required('Required'),
		}),
	patient_gender: yup.string().when('patient', {
		is: 'new_patient',
		then: yup.string().required('Required'),
	}),
	// patient_mobile: yup.number().when('patient', {
	// 	is: 'new_patient',
	// 	then: yup.number().required('Required'),
	// }),
	// patient_email: yup
	// 	.string()
	// 	.email()
	// 	.when('patient', {
	// 		is: 'new_patient',
	// 		then: yup.string().required('Required'),
	// 	}),
	date_scan: yup
		.date()
		.nullable()
		.when('patient', {
			is: 'new_patient',
			then: yup.date().required('Required'),
		}),
	indication_for_scan: yup.string().when('patient', {
		is: 'new_patient',
		then: yup.string().required('Required'),
	}),
	ctdent_patient: yup.string().when('patient', {
		is: 'ctdent_patient',
		then: yup.string().required('Required'),
	}),
});

export const createAppointmentValidationSchema = (
	isReferral,
	activeScansCheckboxes,
	branchId,
) => {
	let validationShape = {
		patientDetails: yup.object().shape({
			patient_title: yup.string().required(),
			patient_full_name: yup
				.string('Must be a string value')
				.required('Required field')
				.max(30, 'Must be at most 30 characters')
				.matches(/^[aA-zZ\s]+$/, 'Only alphabets are allowed for this field '),
			patient_dob: yup.date().required(),
			patient_gender: yup.string().required(),
			// patient_phone: yup
			// 	.string()
			// 	.matches(/^[0-9]+$/, 'Must be only digits')
			// 	.min(11, 'Must be exactly 11 digits')
			// 	.max(11, 'Must be exactly 11 digits')
			// 	.required('Required field'),
			patient_mobile: yup
				.string()
				.matches(/^[0-9]+$/, 'Must be only digits')
				.min(11, 'Must be exactly 11 digits')
				.max(11, 'Must be exactly 11 digits')
				.required('Required field'),
			// patient_email: yup
			// 	.string()
			// 	.email('Enter a valid email')
			// 	.required('Required field'),
			payment: yup.string().required(),
			// pregnancy: yup.string().required(),
			pregnancy: yup.string().when('patient_gender', {
				is: 'female',
				then: yup.string().required(),
			}),
			// patient_booking: isReferral ? yup.string().required() : null,
			// scanned_after: isReferral ? yup.string().required() : null,
			// scanned_after_date: isReferral
			// 	? yup.date().when('scanned_after', {
			// 			is: 'true',
			// 			then: yup.date().required(),
			// 	  })
			// 	: null,
		}),
		patientConsents: yup.boolean().oneOf([true]),
		patientAgrees: yup.boolean().oneOf([true]),
	};

	//Extra validation added only when relevant checkboxes are ticked.
	//Those checkboxes aren't part of the form so can't use yup's "when"
	if (activeScansCheckboxes && activeScansCheckboxes.includes(CBCT)) {
		validationShape.cbctDetails = yup.object().shape({
			radiographic_template: yup.string().required(),
			related_radiographic_template_select: isReferral
				? yup.string().when('radiographic_template', {
						is: 'true',
						then: yup.string().required(),
				  })
				: null,
			area: yup.string().required(),
			doctor_instructions: yup.string().required(),
			scans: yup.array().min(1),
			teethList: yup.array().min(1),
			dyno_3d_scanner:
				branchId === MANCHESTER_BRANCH_ID
					? yup.number().required()
					: yup.number().nullable(),
			// outputs: yup.array().min(1),
		});
	}
	if (activeScansCheckboxes && activeScansCheckboxes.includes(IMAGING2D)) {
		validationShape.imaging2DDetails = yup.object({
			imaging: yup.array().min(1),
		});
		if (activeScansCheckboxes && !activeScansCheckboxes.includes(CBCT)) {
			validationShape.cbctDetails = yup.object().shape({
				radiographic_template: yup.string().required(),
				doctor_instructions: yup.string().required(),
			});
		}
	}
	if (
		activeScansCheckboxes &&
		(activeScansCheckboxes.includes(IMAGING2D) ||
			activeScansCheckboxes.includes(CBCT))
	) {
		validationShape.extraDetails = yup.object({
			justifications: yup.array().min(1),
			extras: yup.array().notRequired(),
			extra_comment: yup.string().when('extras', (extras, schema) => {
				return extras?.includes(56) ? schema.required() : schema.notRequired();
			}),
			extra_comment_uk: yup.string().when('extras', (extras, schema) => {
				return extras?.includes(57) ? schema.required() : schema.notRequired();
			}),
			extra_comment_implant: yup.string().when('extras', (extras, schema) => {
				return extras?.includes(85) ? schema.required() : schema.notRequired();
			}),
		});
	}
	if (activeScansCheckboxes && activeScansCheckboxes.includes(INTRAORAL)) {
		validationShape.intraoralDetails = yup.object({
			intra_oral_scanning_field: yup.string().required(),
			// only when digitalImpression is selected
			cadcam_area: yup.string().when('intra_oral_scanning_field', {
				is: 'digital_impression',
				then: yup.string().required(),
			}),
		});
	}

	const validationSchema = yup.object().shape(validationShape);
	return validationSchema;
};
//TODO: en.js
//DIAGNOCAT FORMS
export const ThirdMolarValidationSchema = yup.object().shape({
	teethList: yup
		.array()
		.min(1, 'Must Select Teeth')
		.max(1, 'Only one teeth')
		.required(REQUIRED),
});

export const STLValidationSchema = yup.object().shape({
	teethList: yup.array().min(1, 'Must Select Teeth').required(REQUIRED),
});

export const EndodonticsImplantologyValidationSchema = yup.object().shape({
	sliceThickness: yup.string().required(REQUIRED),
	sliceSteps: yup.string().required(REQUIRED),
	teethList: yup.array().min(1, 'Must Select Teeth').required(REQUIRED),
});

export const getAuthValidationSchema = (requiredMessage) => {
	return yup.object().shape({
		username: yup
			.string()
			.required(requiredMessage)
			.matches(emailRegExp, i18next.t('forms.validators.emailInvalid'))
			.email(i18next.t('forms.validators.emailInvalid')),
		password: yup
			.string()
			.required(requiredMessage)
			.matches(passwordRegExp, PASSWORD_MESSAGE),
	});
};

export const signupValidationSchema = (requiredMessage) => {
	const phoneRegExp =
		/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

	return yup.object().shape({
		gdc_number: yup
			.string()
			.required(requiredMessage)
			.min(5, 'Must be atleast 5 digits')
			.max(8, 'Must be less than 8 digits'),
		speciality: yup.string().required(requiredMessage),
		forename: yup.string().required(requiredMessage),
		surname: yup.string().required(requiredMessage),
		p_name: yup.string().required(requiredMessage),
		p_address_1: yup.string().required(requiredMessage),
		p_city: yup.string().required(requiredMessage),
		p_postcode: yup.string().required(requiredMessage),
		contact_number: yup
			.string()
			.required(requiredMessage)
			.matches(
				phoneRegExp,
				i18next.t('forms.validators.contactNumberIsInvalid'),
			),
		mobile_number: yup
			.string()
			.required(requiredMessage)
			.min(11, 'Must be exactly 11 digits')
			.max(11, 'Must be exactly 11 digits'),
		email_1: yup
			.string()
			.required(requiredMessage)
			.matches(emailRegExp, i18next.t('forms.validators.emailInvalid'))
			.email(i18next.t('forms.validators.emailInvalid')),
		password: yup
			.string()
			.required(requiredMessage)
			.min(minPasswordLength, `Password can't be shorter than ${minPasswordLength} characters.`)
			.max(maxPasswordLength, `Password can't be longer than ${maxPasswordLength} characters.`)
			.matches(passwordRegExp, PASSWORD_MESSAGE),
		branch_id: yup.string().required(requiredMessage),
		hear_from: yup.string().required(requiredMessage),
		confirmPassword: yup
			.string()
			.required(requiredMessage)
			.matches(passwordRegExp, PASSWORD_MESSAGE)
			.oneOf(
				[yup.ref('password')],
				i18next.t('forms.validators.passwordShouldBeSame'),
			),
		confirmEmail: yup
			.string()
			.required(requiredMessage)
			.oneOf(
				[yup.ref('email_1')],
				i18next.t('forms.validators.emailShouldBeSame'),
			),
		// accept_terms: yup.string().required(requiredMessage),
		accept_terms: yup.bool().oneOf([true], requiredMessage),
		// contact_number: yup.string().matches(phoneRegExp, i18next.t('forms.validators.contactNumberIsInvalid')),
		//  acceptTerms: yup.string().required(requiredMessage),
	});
};
export const getPatientFormValidationSchema = (
	requiredMessage,
	fieldTooShortMessage,
	fieldTooLongMessage,
) => {
	return yup.object().shape({
		firstName: nameValidator.required(requiredMessage),
		lastName: nameValidator.required(requiredMessage),
		dateOfBirth: yup.date().required(requiredMessage),
		// referringDoctor: yup
		//     .string()
		//     .min(2, fieldTooShortMessage)
		//     .max(50, fieldTooLongMessage)
		//     .required(requiredMessage),
		gender: yup.string().required(requiredMessage),
	});
};

export const getSendEmailFormValidationSchema = (requiredMessage) => {
	return yup.object().shape({
		pname: yup.string().required(requiredMessage),
		pemail: yup
			.string()
			.required(requiredMessage)
			.matches(emailRegExp, i18next.t('forms.validators.emailInvalid'))
			.email(i18next.t('forms.validators.emailInvalid')),
	});
};

export const forgotPasswordSchema = (requiredMessage) => {
	return yup.object().shape({
		email_1: yup
			.string()
			.required(requiredMessage)
			.matches(emailRegExp, i18next.t('forms.validators.emailInvalid'))
			.email(i18next.t('forms.validators.emailInvalid')),
	});
};

export const resetPasswordSchema = (requiredMessage) => {
	return yup.object().shape({
		password: yup
			.string()
			.required(requiredMessage)
			.min(minPasswordLength, `Password can't be shorter than ${minPasswordLength} characters.`)
			.max(maxPasswordLength, `Password can't be longer than ${maxPasswordLength} characters.`)
			.matches(passwordRegExp, PASSWORD_MESSAGE),
		confirmPassword: yup
			.string()
			.required(requiredMessage)
			.matches(passwordRegExp, PASSWORD_MESSAGE)
			.oneOf(
				[yup.ref('password')],
				i18next.t('forms.validators.passwordShouldBeSame'),
			),
	});
};

export const myProfileValidationSchema = (
	requiredMessage,
	isPartner = false,
) => {
	const phoneRegExp =
		/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

	const partnerNameExtenstion = isPartner
		? yup.string().nullable().required(requiredMessage)
		: yup.string().nullable();
	// const partnerCompany = yup.string().nullable();
	const partnerRadiologytTitle = isPartner
		? yup.string().nullable().required(requiredMessage)
		: yup.string().nullable();
	const partnerGdcNumber = isPartner
		? yup.string().nullable()
		: yup
				.string()
				.required(requiredMessage)
				.min(5, 'Must be atleast 5 digits')
				.max(8, 'Must be less than 8 digits');
	const partnerRegistrationNumber = isPartner
		? yup.string().required(requiredMessage)
		: yup.string().nullable();

	return yup.object().shape({
		gdc_number: partnerGdcNumber,
		dyno_registration_number: partnerRegistrationNumber,
		speciality: isPartner
			? yup.string().nullable()
			: yup.string().required(requiredMessage),
		forename: yup.string().required(requiredMessage),
		surname: yup.string().required(requiredMessage),
		p_name: yup.string().required(requiredMessage),
		branch_id: yup.string().required(requiredMessage),
		p_address_1: yup.string().required(requiredMessage),
		p_city: yup.string().required(requiredMessage),
		p_postcode: yup.string().required(requiredMessage),
		contact_number: yup
			.string()
			.min(11, 'Must be exactly 11 digits')
			.max(11, 'Must be exactly 11 digits')
			.required(requiredMessage)
			.matches(
				phoneRegExp,
				i18next.t('forms.validators.contactNumberIsInvalid'),
			),
		mobile_number: yup
			.string()
			.min(11, 'Must be exactly 11 digits')
			.max(11, 'Must be exactly 11 digits'),
		email_1: yup
			.string()
			.required(requiredMessage)
			.matches(emailRegExp, i18next.t('forms.validators.emailInvalid'))
			.email(i18next.t('forms.validators.emailInvalid')),
		email_2: yup.string().email(i18next.t('forms.validators.emailInvalid')),
		email_3: yup.string().email(i18next.t('forms.validators.emailInvalid')),
		hasChangePassword: yup.boolean(),
		password: yup
			.string()
			.when("hasChangePassword", {
        is: true,
        then: yup.string()
				.required(requiredMessage)
				.matches(passwordRegExp, PASSWORD_MESSAGE)
				.min(6, `Password can't be shorter than 6 characters.`)
				.max(maxPasswordLength, `Password can't be longer than ${maxPasswordLength} characters.`)
      }),
		newPassword: yup
			.string()
			.when("hasChangePassword", {
        is: true,
        then: yup.string()
				.required(requiredMessage)
				.matches(passwordRegExp, PASSWORD_MESSAGE)
				.min(minPasswordLength, `Password can't be shorter than ${minPasswordLength} characters.`)
				.max(maxPasswordLength, `Password can't be longer than ${maxPasswordLength} characters.`)
      }),
		confirmPassword: yup
			.string()
			.when("hasChangePassword", {
        is: true,
        then: yup
				.string()
				.required(requiredMessage)
				.matches(passwordRegExp, PASSWORD_MESSAGE)
				.oneOf(
					[yup.ref('newPassword'), null],
					i18next.t('forms.validators.passwordShouldBeSame'),
				)
      }),
		dyno_name_extension: partnerNameExtenstion,
		// dyno_company: partnerCompany,
		dyno_radiology_title: partnerRadiologytTitle,
	});
};

export const billingInvoiceEmailValidationSchema = () =>
	yup.object().shape({
		additional_invoices_email: yup.boolean().required(REQUIRED_STATIC),
		invoice_email: yup.string().when('additional_invoices_email', {
			is: true,
			then: yup.string().email('Enter a valid email').required(REQUIRED_STATIC),
		}),
	});

export const billingAddressValidationSchema = (isDifferentAddress) =>
	yup.object().shape({
		b_address_1: isDifferentAddress
			? yup.string().required(REQUIRED_STATIC)
			: null,
		b_city: isDifferentAddress ? yup.string().required(REQUIRED_STATIC) : null,
		b_name: isDifferentAddress ? yup.string().required(REQUIRED_STATIC) : null,
		b_postcode: isDifferentAddress
			? yup.string().required(REQUIRED_STATIC)
			: null,
	});

export const cardDetailValidationSchema = (isCardAdded) =>
	yup.object().shape({
		cardName: !isCardAdded ? yup.string().required(REQUIRED_STATIC) : null,
	});

export const masterPricelistValidationSchema = () =>
	yup.object().shape({
		sectional_price: yup.number().required(REQUIRED_STATIC),
		quadrant_price: yup.number().required(REQUIRED_STATIC),
		one_jaw_price: yup.number().required(REQUIRED_STATIC),
		two_jaws_price: yup.number().required(REQUIRED_STATIC),
		ortho_price: yup.number().required(REQUIRED_STATIC),
	});

export const appointmentPriceValidationSchema = (isPrice) =>
	isPrice
		? yup.object().shape({
				appointment_price: yup.number().required(REQUIRED_STATIC),
		  })
		: yup.object().shape({
				payment: yup.string().required(REQUIRED_STATIC),
		  });

export const adminIPValidationSchema = () =>
	yup.object().shape({
		ip: yup.string().required(REQUIRED_STATIC),
		comment: yup.string().required(REQUIRED_STATIC),
	});

export const practiceAddressValidationSchema = () =>
	yup.object().shape({
		p_name: yup.string().required(REQUIRED_STATIC),
		p_address_1: yup.string().required(REQUIRED_STATIC),
		p_city: yup.string().required(REQUIRED_STATIC),
		p_postcode: yup.string().required(REQUIRED_STATIC),
	});

export const appointmentDeleteValidationSchema = () =>
	yup.object().shape({
		dyno_archive_reason: yup.string().required(REQUIRED_STATIC),
	});

export const invisibleSharesValidationSchema = () =>
	yup.object().shape({
		invisible_shares: yup.string().required(REQUIRED_STATIC),
	});

export const detailedReportValidationSchema = () =>
	yup.object().shape({
		start: yup.date().required(REQUIRED_STATIC),
		end: yup.date().required(REQUIRED_STATIC),
	});

export const partnersaccountsValidationSchema = () =>
	yup.object().shape({
		partners_accounts: yup.string().required(REQUIRED_STATIC),
	});

export const partnersCreateReportValidationSchema = () =>
	yup.object().shape({
		patient_name: yup.string().required(REQUIRED_STATIC),
		patient_dob: yup.string().required(REQUIRED_STATIC),
		doctor_name: yup.string().required(REQUIRED_STATIC),
		clinical_indications: yup.string().required(REQUIRED_STATIC),
		finding: yup.string(REQUIRED_STATIC).required(REQUIRED_STATIC),
		picture_text_1: yup
			.string()
			.nullable()
			.when('picture_1', (picture_1) => {
				if (picture_1) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_2: yup
			.string()
			.nullable()
			.when('picture_2', (picture_2) => {
				if (picture_2) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_3: yup
			.string()
			.nullable()
			.when('picture_3', (picture_3) => {
				if (picture_3) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_4: yup
			.string()
			.nullable()
			.when('picture_4', (picture_4) => {
				if (picture_4) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_5: yup
			.string()
			.nullable()
			.when('picture_5', (picture_5) => {
				if (picture_5) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_6: yup
			.string()
			.nullable()
			.when('picture_6', (picture_6) => {
				if (picture_6) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_7: yup
			.string()
			.nullable()
			.when('picture_7', (picture_7) => {
				if (picture_7) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_8: yup
			.string()
			.nullable()
			.when('picture_8', (picture_8) => {
				if (picture_8) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_9: yup
			.string()
			.nullable()
			.when('picture_9', (picture_9) => {
				if (picture_9) return yup.string().required(REQUIRED_STATIC);
			}),
		picture_text_10: yup
			.string()
			.nullable()
			.when('picture_10', (picture_10) => {
				if (picture_10) return yup.string().required(REQUIRED_STATIC);
			}),
	});
